const isClient = typeof window !== "undefined";

type CoordinateType = "lat" | "lng";
const OVERRIDE_LAT_KEY = "DISCOVERY_OVERRIDE_LAT";
const OVERRIDE_LNG_KEY = "DISCOVERY_OVERRIDE_LNG";
const OVERRIDE_LABEL_KEY = "DISCOVERY_OVERRIDE_LABEL";
const CURRENT_LAT_KEY = "DEVICE_LOCATION_LAT";
const CURRENT_LNG_KEY = "DEVICE_LOCATION_LNG";
const PROFILE_SOURCE_KEY = "PROFILE_LOCATION_SOURCE";
export type ProfileLocationSource = "manual_selection" | "browser_location" | "unknown";

export const getProfileLocationSource = (): ProfileLocationSource => {
	const source = isClient ? window.localStorage.getItem(PROFILE_SOURCE_KEY) : null;
	return source === "manual_selection" || source === "browser_location" ? source : "unknown";
};

export const setProfileLocationSource = (source: ProfileLocationSource): void => {
	if (isClient) window.localStorage.setItem(PROFILE_SOURCE_KEY, source);
};
const LEGACY_DISCOVERY_LAT_KEY = "LOCATION_LAT";
const LEGACY_DISCOVERY_LNG_KEY = "LOCATION_LNG";
const LEGACY_COUNTRY_LAT_KEY = "COUNTRY_LOCATION_LAT";
const LEGACY_COUNTRY_LNG_KEY = "COUNTRY_LOCATION_LNG";

const INVALID_STRINGS = new Set(["", "null", "undefined", "nan"]);

const normalizeRawStorageValue = (value: string | null): string => {
	if (!value) return "";
	let cleaned = value.trim();
	if (!cleaned) return "";

	// Support values that were previously saved with JSON.stringify.
	try {
		const parsed = JSON.parse(cleaned);
		if (typeof parsed === "number" || typeof parsed === "string") {
			cleaned = String(parsed).trim();
		}
	} catch {
		// Keep original value if it is not valid JSON.
	}

	if (!cleaned) return "";
	if (INVALID_STRINGS.has(cleaned.toLowerCase())) return "";
	return cleaned;
};

const normalizeCountryName = (value: string): string =>
	value
		.trim()
		.toLowerCase()
		.replace(/[^\p{L}\p{N}\s]/gu, "")
		.replace(/\s+/g, " ");

const toValidCoordinate = (
	value: string | null,
	coordinateType: CoordinateType
): string => {
	const normalized = normalizeRawStorageValue(value);
	if (!normalized) return "";
	const numeric = Number(normalized);
	if (!Number.isFinite(numeric)) return "";

	if (coordinateType === "lat" && (numeric < -90 || numeric > 90)) return "";
	if (coordinateType === "lng" && (numeric < -180 || numeric > 180)) return "";
	return numeric.toString();
};

const haversineKm = (
	lat1: number,
	lng1: number,
	lat2: number,
	lng2: number
): number => {
	const toRad = (degrees: number) => (degrees * Math.PI) / 180;
	const earthRadiusKm = 6371;
	const dLat = toRad(lat2 - lat1);
	const dLng = toRad(lng2 - lng1);
	const a =
		Math.sin(dLat / 2) * Math.sin(dLat / 2) +
		Math.cos(toRad(lat1)) *
			Math.cos(toRad(lat2)) *
			Math.sin(dLng / 2) *
			Math.sin(dLng / 2);
	return earthRadiusKm * (2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)));
};

export const getStoredCoordinate = (
	key: string,
	coordinateType: CoordinateType
): string => {
	if (!isClient) return "";
	return toValidCoordinate(window.localStorage.getItem(key), coordinateType);
};

export const setStoredCoordinate = (
	key: string,
	value: string | number | null | undefined,
	coordinateType: CoordinateType
): void => {
	if (!isClient) return;
	if (value === null || value === undefined || value === "") {
		window.localStorage.removeItem(key);
		return;
	}
	const normalized = toValidCoordinate(String(value), coordinateType);
	if (!normalized) return;
	window.localStorage.setItem(key, normalized);
};

export const getDiscoveryCoordinates = () => ({
	...getEffectiveDiscoveryCoordinates(),
});

export const getCountryCoordinates = () => ({
	...getCurrentRealCoordinates(),
});

export const getCurrentRealCoordinates = () => ({
	lat: getStoredCoordinate(CURRENT_LAT_KEY, "lat"),
	lng: getStoredCoordinate(CURRENT_LNG_KEY, "lng"),
});

export const getDeviceCoordinates = () => getCurrentRealCoordinates();

export const getManualDiscoveryOverrideCoordinates = () => ({
	lat: getStoredCoordinate(OVERRIDE_LAT_KEY, "lat"),
	lng: getStoredCoordinate(OVERRIDE_LNG_KEY, "lng"),
});

export const hasActiveManualDiscoveryOverride = () => {
	const override = getManualDiscoveryOverrideCoordinates();
	return Boolean(override.lat && override.lng);
};

export const getEffectiveDiscoveryCoordinates = () => {
	const override = getManualDiscoveryOverrideCoordinates();
	if (override.lat && override.lng) {
		return override;
	}
	return getCurrentRealCoordinates();
};

export const hasUsableDiscoveryLocation = () => {
	const { lat, lng } = getEffectiveDiscoveryCoordinates();
	if (!lat || !lng) return false;
	const pair = `${Number(lat)},${Number(lng)}`;
	return !["0,0", "-37.820797,144.959183", "-37.820797,144.820797"].includes(pair);
};

export const getDistanceOriginCoordinates = () => getCurrentRealCoordinates();

export const getManualDiscoveryOverrideLabel = (): string => {
	if (!isClient) return "";
	return normalizeRawStorageValue(window.localStorage.getItem(OVERRIDE_LABEL_KEY));
};

export const setCurrentRealCoordinates = (
	lat: string | number | null | undefined,
	lng: string | number | null | undefined
): void => {
	setStoredCoordinate(CURRENT_LAT_KEY, lat, "lat");
	setStoredCoordinate(CURRENT_LNG_KEY, lng, "lng");
};

export const setDiscoveryCoordinates = (
	lat: string | number | null | undefined,
	lng: string | number | null | undefined
): void => {
	setCurrentRealCoordinates(lat, lng);
};

export const setCountryCoordinates = (
	lat: string | number | null | undefined,
	lng: string | number | null | undefined
): void => {
	setCurrentRealCoordinates(lat, lng);
};

export const setDeviceCoordinates = (
	lat: string | number | null | undefined,
	lng: string | number | null | undefined
): void => {
	setCurrentRealCoordinates(lat, lng);
};

export const setManualDiscoveryOverrideCoordinates = (
	lat: string | number | null | undefined,
	lng: string | number | null | undefined,
	label?: string | null
): void => {
	setStoredCoordinate(OVERRIDE_LAT_KEY, lat, "lat");
	setStoredCoordinate(OVERRIDE_LNG_KEY, lng, "lng");
	if (!isClient) return;
	const cleanedLabel = normalizeRawStorageValue(label ?? null);
	if (!cleanedLabel) {
		window.localStorage.removeItem(OVERRIDE_LABEL_KEY);
		return;
	}
	window.localStorage.setItem(OVERRIDE_LABEL_KEY, cleanedLabel);
};

export const clearManualDiscoveryOverride = (): void => {
	if (!isClient) return;
	window.localStorage.removeItem(OVERRIDE_LAT_KEY);
	window.localStorage.removeItem(OVERRIDE_LNG_KEY);
	window.localStorage.removeItem(OVERRIDE_LABEL_KEY);
};

const clearLegacyLocationKeys = (): void => {
	if (!isClient) return;
	window.localStorage.removeItem(LEGACY_DISCOVERY_LAT_KEY);
	window.localStorage.removeItem(LEGACY_DISCOVERY_LNG_KEY);
	window.localStorage.removeItem(LEGACY_COUNTRY_LAT_KEY);
	window.localStorage.removeItem(LEGACY_COUNTRY_LNG_KEY);
};

export const isCrossCountryDiscovery = (thresholdKm = 1000): boolean => {
	const discovery = getDiscoveryCoordinates();
	const device = getDistanceOriginCoordinates();
	if (!discovery.lat || !discovery.lng || !device.lat || !device.lng) {
		return false;
	}
	const distanceKm = haversineKm(
		Number(discovery.lat),
		Number(discovery.lng),
		Number(device.lat),
		Number(device.lng)
	);
	return Number.isFinite(distanceKm) && distanceKm > thresholdKm;
};

export const getSelectedDiscoveryCountry = (): string => {
	if (!isClient) return "";
	const rawLocation =
		getManualDiscoveryOverrideLabel() || window.localStorage.getItem("userLocation") || "";
	const trimmed = rawLocation.trim();
	if (!trimmed) return "";
	const parts = trimmed
		.split(",")
		.map((part) => part.trim())
		.filter(Boolean);
	return parts.length ? parts[parts.length - 1] : trimmed;
};

export const isSelectedCountryDifferent = (deviceCountry: string): boolean => {
	const selectedCountry = getSelectedDiscoveryCountry();
	if (!selectedCountry || !deviceCountry) return false;
	return (
		normalizeCountryName(selectedCountry) !== normalizeCountryName(deviceCountry)
	);
};

const extractCountryLabel = (value?: string | null): string => {
	const cleaned = (value ?? "").trim();
	if (!cleaned) return "";
	const parts = cleaned
		.split(",")
		.map((part) => part.trim())
		.filter(Boolean);
	return parts.length ? parts[parts.length - 1] : cleaned;
};

export const getDisplayLocationLabel = (user?: {
	city?: string | null;
	country?: string | null;
	discovery_override_label?: string | null;
}) => {
	const overrideLabel = (user?.discovery_override_label ?? "").trim();
	if (overrideLabel) return overrideLabel;
	const cityLabel = extractCountryLabel(user?.city);
	if (cityLabel) return cityLabel;
	return (user?.country ?? "").trim();
};

export const syncLocationStateFromUser = (user?: {
	lat?: string | number | null;
	lng?: string | number | null;
	city?: string | null;
	country?: string | null;
	discovery_override_lat?: string | number | null;
	discovery_override_lng?: string | number | null;
	discovery_override_label?: string | null;
	profile_location_source?: ProfileLocationSource | null;
}) => {
	if (!isClient || !user) return;
	setCurrentRealCoordinates(user.lat, user.lng);
	setProfileLocationSource(user.profile_location_source || "unknown");
	const hasOverride =
		toValidCoordinate(String(user.discovery_override_lat ?? ""), "lat") &&
		toValidCoordinate(String(user.discovery_override_lng ?? ""), "lng");
	if (hasOverride) {
		setManualDiscoveryOverrideCoordinates(
			user.discovery_override_lat,
			user.discovery_override_lng,
			user.discovery_override_label ?? ""
		);
	} else {
		clearManualDiscoveryOverride();
	}
	const label = getDisplayLocationLabel(user);
	if (label) {
		window.localStorage.setItem("userLocation", label);
	} else {
		window.localStorage.removeItem("userLocation");
	}
	clearLegacyLocationKeys();
};

export const getMatchLocationParams = () => {
	const search = getEffectiveDiscoveryCoordinates();
	const distanceOrigin = getDistanceOriginCoordinates();
	return {
		user_lat: search.lat,
		user_lng: search.lng,
		distance_origin_lat: distanceOrigin.lat,
		distance_origin_lng: distanceOrigin.lng,
	};
};
